import { Text } from 'app/design/primitives/text';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColorValue } from 'react-native';
import { Pressable } from 'react-native';
import { Column } from 'app/design/primitives/box';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import type { TransactionMode } from 'app/types/listing';
import { GroupType } from 'app/types/group-base';
import { WhiteLabel } from 'app/types/white-labels';
import { objectsAreEquivalent, ICON_STROKE_WIDTH } from 'app/utils/constants';
import { ListingsFiltersSheetContent } from 'app/screens/billboard/all-listings/components/listings-filters-sheet-content';
import {
  applyDefaultFilterValues,
  DEFAULT_TRANSACTION_MODES,
} from 'app/screens/billboard/all-listings/components/listings-filters-defaults';
import { useOnePreference, useUpsertOnePreference, MAX_RADIUS_IN_METERS } from 'app/network/preferences';
import { useSearchCenter } from 'app/hooks/use-search-center';
import { type PreferenceNameToValue } from 'app/types/preferences';
import type { LatLng } from 'react-native-maps';
import MapSheet from 'app/atoms/map-sheet';

import { useAtom, useAtomValue } from 'jotai';
import {
  addressesLoadedAtom,
  isLoggedInAtom,
  preferencesLoadedAtom,
  selectedCategoryAtom,
  selectedServiceCategoryAtom,
  selectedTagAtom,
} from 'app/atoms';
import { ListingsSortBy, ListingSubtype, ListingType } from 'app/types/listing';
import type { Category, ServiceCategory } from 'app/types/category';
import { useAllCategories, useAllServiceCategories, useSimplifiedServices } from 'app/network/category';
import { type Tag, useAllTags, useAvailableSubtypes, useSelectedGroupsAndAddresses } from 'app/network/listing';
import { useStoredValue } from 'app/lib/storage';
import { StorageKey } from 'app/lib/storageKeys';
import type { Group } from 'app/types/group';
import { useAllGroups } from 'app/network/groups';
import type { Address } from 'app/types/address';
import { useAllAddresses } from 'app/network/address';
import type * as LFTypes from 'app/types/listings-filters';
import { useUser } from 'app/network/user';
import { useUISize } from 'app/utils/web-helpers';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconAdjustments } from '@tabler/icons-react-native';
import { SearchAddressesMap } from './search-addresses-map';
const theme = createDSTheme();

export const EmptyBadge = ' ';
const EMPTY_STRING_IDS: string[] = [];
const EMPTY_LISTING_SUBTYPES: ListingSubtype[] = [];
const EMPTY_TRANSACTION_MODES: TransactionMode[] = [];

function isSameDraftSource<T>(left: T, right: T) {
  if (Object.is(left, right)) return true;
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
  return left.every((value, index) => Object.is(value, right[index]));
}

/** Keeps an editable draft while immediately adopting a new upstream value. */
function useSourceDraft<T>(source: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [draft, setDraft] = useState(() => ({ source, value: source }));
  let currentDraft = draft;
  if (!isSameDraftSource(draft.source, source)) {
    currentDraft = { source, value: source };
    setDraft(currentDraft);
  }

  const setValue: React.Dispatch<React.SetStateAction<T>> = (nextValue) => {
    setDraft((previousDraft) => {
      const currentValue = isSameDraftSource(previousDraft.source, source) ? previousDraft.value : source;
      return {
        source,
        value: typeof nextValue === 'function' ? (nextValue as (value: T) => T)(currentValue) : nextValue,
      };
    });
  };

  return [currentDraft.value, setValue];
}

const ListingsFiltersPanel = memo(function ListingsFiltersPanelComponent({
  appliedFilters,
  defaultDistance,
  listingType,
  open,
  onClose,
  showInBottomSheet,
  hideCategoryAndTagFilters = false,
}: Readonly<{
  appliedFilters: LFTypes.FilterPreferences;
  defaultDistance?: number;
  listingType: ListingType;
  open: boolean;
  onClose: () => void;
  showInBottomSheet: boolean;
  hideCategoryAndTagFilters?: boolean;
}>) {
  const { t } = useTranslation('listings-filters');
  const { isSmallUI } = useUISize();

  const { availableValues } = appliedFilters;

  // appliedFilters is the currently applied filters
  // filtersCopy is the filters on screen, that can be applied when the user presses the "Save" button
  const filtersCopy = useFiltersCopy(appliedFilters, defaultDistance);
  const {
    selectedAddresses,
    selectedGroups,
    selectedCategories,
    selectedTags,
    selectedServiceCategories,
    selectedSubtypes,
    hideFoundRequests,
    dismissedOnly,
    hideOffersWithFee,
    selectedTransactionModes,
    toggleAddress,
    toggleGroup,
    toggleSubtype,
    toggleFoundRequests,
    toggleDismissedRequests,
    toggleOffersWithFee,
    toggleTransactionMode,
    searchDistance,
    searchCenter,
  } = filtersCopy;

  const onSave = useCallback(
    (
      newSearchDistance?: number,
      newSearchCenter?: LatLng,
      newCategories?: string[],
      newTags?: string[],
      newServiceCategories?: string[],
      newSubtypes?: ListingSubtype[],
      newHideFoundRequests?: boolean,
      newDismissedOnly?: boolean,
      newHideOffersWithFee?: boolean,
      newTransactionModes?: TransactionMode[],
    ) => {
      // Apply filters (to filterPreferences)
      filtersCopy.onSave(
        newSearchDistance,
        newSearchCenter,
        newCategories,
        newTags,
        newServiceCategories,
        newSubtypes,
        newHideFoundRequests,
        newDismissedOnly,
        newHideOffersWithFee,
        newTransactionModes,
      );
      onClose();
    },
    [filtersCopy, onClose],
  );

  const onReset = useCallback(() => {
    filtersCopy.resetFilters();
    onClose();
  }, [filtersCopy, onClose]);

  const addresses = useMemo(() => availableValues.addresses ?? [], [availableValues.addresses]);
  const groups = useMemo(() => {
    const availableGroups = availableValues.groups ?? [];
    return isWhiteLabel(WhiteLabel.HauteGaspesie)
      ? availableGroups.filter((group) => group.type === GroupType.sector)
      : availableGroups;
  }, [availableValues.groups]);

  const simplifiedServices = useSimplifiedServices();

  const hideGroups = useMemo(
    () => isFeatureEnabled(FeatureFlag.justOneClub) || groups.length + addresses.length <= 1,
    [addresses, groups],
  );

  const listingSubtypes = useMemo(
    () =>
      availableValues.subtypes.filter(
        // Service subtype is only available for requests filters
        (st) => st !== ListingSubtype.service || listingType === ListingType.request,
      ),
    [availableValues.subtypes, listingType],
  );

  const { data: user } = useUser();
  const loanFeeAvailable = useMemo(
    () => isFeatureEnabled(FeatureFlag.loanFee, user?.featureFlags),
    [user?.featureFlags],
  );

  return (
    <MapSheet
      audienceText={t('audience')}
      initialRadius={searchDistance}
      renderRadiusPreview={
        addresses.length > 1
          ? (radius) => (
              <SearchAddressesMap
                addresses={
                  selectedAddresses.length > 0
                    ? addresses.filter((address) => selectedAddresses.includes(address.id))
                    : addresses
                }
                radius={radius}
              />
            )
          : undefined
      }
      layout={showInBottomSheet ? 'bottom-sheet' : 'inline'}
      initialRadiusCenter={{
        latitude: searchCenter.radiusCenterLatitude,
        longitude: searchCenter.radiusCenterLongitude,
      }}
      isSaving={appliedFilters.isSaving}
      onClose={onClose}
      onReset={onReset}
      onSave={onSave}
      open={open}
      title={t(`title.${listingType}`)}
      variant={{
        interaction:
          (selectedAddresses.length === 0 && selectedGroups.length === 0) || listingType === ListingType.request
            ? 'disabled'
            : 'enabled',
        map: addresses.length > 0 ? 'visible' : 'hidden',
        audience: 'hidden',
        maximumLabel: 'maximum',
      }}
    >
      <Column height={theme.spacing['spacing.general.x2']} />
      <ListingsFiltersSheetContent
        addresses={addresses}
        dismissedOnly={dismissedOnly}
        groups={groups}
        hideFoundRequests={hideFoundRequests}
        hideGroups={hideGroups}
        hideOffersWithFee={hideOffersWithFee}
        hideCategoryAndTagFilters={hideCategoryAndTagFilters}
        isSmallUI={isSmallUI}
        listingSubtypes={listingSubtypes}
        listingType={listingType}
        loanFeeAvailable={loanFeeAvailable}
        onSave={onSave}
        selectedAddresses={selectedAddresses}
        selectedCategories={selectedCategories}
        selectedGroups={selectedGroups}
        selectedServiceCategories={selectedServiceCategories}
        selectedSubtypes={selectedSubtypes}
        selectedTags={selectedTags}
        selectedTransactionModes={selectedTransactionModes}
        simplifiedServices={simplifiedServices}
        t={t}
        toggleAddress={toggleAddress}
        toggleDismissedRequests={toggleDismissedRequests}
        toggleFoundRequests={toggleFoundRequests}
        toggleGroup={toggleGroup}
        toggleOffersWithFee={toggleOffersWithFee}
        toggleSubtype={toggleSubtype}
        toggleTransactionMode={toggleTransactionMode}
      />
    </MapSheet>
  );
});

const ListingsFilters = memo(
  function ListingsFiltersComponent({
    appliedFilters,
    onOpen,
    defaultDistance,
    color,
    listingType,
    numFilters,
  }: Readonly<{
    appliedFilters: LFTypes.FilterPreferences;
    onOpen?: () => void;
    defaultDistance?: number;
    color?: ColorValue;
    listingType: ListingType;
    numFilters: number | typeof EmptyBadge;
  }>) {
    // Is the filters modal (bottom sheet) visible
    const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);

    const showModal = useCallback(() => {
      onOpen?.();
      if (isFilterModalVisible) {
        setIsFilterModalVisible(false);
      }
      setTimeout(() => setIsFilterModalVisible(true), 10);
    }, [isFilterModalVisible, onOpen]);

    const showBadge = numFilters && (numFilters === EmptyBadge || numFilters > 0);

    return (
      <>
        <Column>
          <Pressable
            onPress={showModal}
            style={{
              backgroundColor: theme.colors['surface.inverse.tertiary-neutral'],
              borderRadius: theme.spacing['spacing.general.x2'],
              paddingHorizontal: theme.spacing['spacing.general.x2'],
              marginRight: showBadge ? 4 : 0,
              height: 30 + theme.spacing['spacing.general.x2'],
              width: 30 + 2 * theme.spacing['spacing.general.x2'],
              justifyContent: 'center',
            }}
            hitSlop={10}
          >
            <IconAdjustments strokeWidth={ICON_STROKE_WIDTH} size={30} color={color} />
          </Pressable>

          {showBadge ? (
            <Column
              alignItems="center"
              aspectRatio={1}
              backgroundColor="surface.brand.default"
              borderRadius="radius.xl"
              height={16}
              justifyContent="center"
              position="absolute"
              right={0}
              top={-4}
            >
              <Text color="surface.default" fontSize={10} fontWeight="bold">
                {numFilters}
              </Text>
            </Column>
          ) : null}
        </Column>

        <ListingsFiltersPanel
          appliedFilters={appliedFilters}
          defaultDistance={defaultDistance}
          listingType={listingType}
          onClose={() => setIsFilterModalVisible(false)}
          open={isFilterModalVisible}
          showInBottomSheet
        />
      </>
    );
  },
  (a, b) => {
    return objectsAreEquivalent(
      { values: [a.appliedFilters.forQuery, a.defaultDistance, a.color, a.listingType, a.numFilters] },
      { values: [b.appliedFilters.forQuery, b.defaultDistance, b.color, b.listingType, b.numFilters] },
    );
  },
);

export { ListingsFilters, ListingsFiltersPanel };

function useFilterPreferencesAddresses(): LFTypes.FilterPreferencesAddresses & LFTypes.FilterPreferencesLoaded {
  const addressesState = useState<Address['id'][] | undefined>(undefined);

  const { selectedAddresses, setSelectedAddresses, loaded } = useSelectedGroupsAndAddresses(undefined, addressesState);

  return {
    addressesState,
    selectedAddresses,
    setSelectedAddresses,
    loaded,
  };
}

function useFilterPreferencesGroups(): LFTypes.FilterPreferencesGroups & LFTypes.FilterPreferencesLoaded {
  const groupsState = useState<Group['id'][] | undefined>(undefined);

  const { selectedGroups, loaded } = useSelectedGroupsAndAddresses(groupsState);

  return {
    groupsState,
    selectedGroups,
    loaded,
  };
}

function useFilterPreferencesCategories(
  defaultSelectedCategory?: Category['id'],
): LFTypes.FilterPreferencesCategories & LFTypes.FilterPreferencesLoaded {
  const categoriesState = useAtom(selectedCategoryAtom);

  const [selectedCategories, , , loaded] = useStoredValue<Category['id'][]>(
    StorageKey.ListingsFiltersCategories,
    defaultSelectedCategory ? [defaultSelectedCategory] : [],
    categoriesState,
  );

  return {
    categoriesState,
    selectedCategories,
    loaded,
  };
}

function useFilterPreferencesTags(
  defaultSelectedTag?: Tag['id'],
): LFTypes.FilterPreferencesTags & LFTypes.FilterPreferencesLoaded {
  const tagsState = useAtom(selectedTagAtom);

  const [selectedTags, , , loaded] = useStoredValue<Tag['id'][]>(
    StorageKey.ListingsFiltersTags,
    defaultSelectedTag ? [defaultSelectedTag] : [],
    tagsState,
  );

  return {
    tagsState,
    selectedTags,
    loaded,
  };
}

function useFilterPreferencesServiceCategories(): LFTypes.FilterPreferencesServiceCategories &
  LFTypes.FilterPreferencesLoaded {
  const serviceCategoriesState = useAtom(selectedServiceCategoryAtom);

  const [selectedServiceCategories, , , loaded] = useStoredValue<ServiceCategory['id'][]>(
    StorageKey.ListingsFiltersServiceCategories,
    [],
    serviceCategoriesState,
  );

  return {
    serviceCategoriesState,
    selectedServiceCategories,
    loaded,
  };
}

function useFilterPreferencesSubtypes(
  availableSubtypes: ListingSubtype[],
): LFTypes.FilterPreferencesSubtypes & LFTypes.FilterPreferencesLoaded {
  const subtypesState = useState<ListingSubtype[] | undefined>([]);

  const [selectedSubtypes, , , loaded] = useStoredValue<ListingSubtype[]>(
    StorageKey.ListingsFiltersSubtypes,
    availableSubtypes,
    subtypesState,
  );

  return {
    subtypesState,
    selectedSubtypes,
    loaded,
  };
}

function useFilterPreferencesHideFoundRequests(): LFTypes.FilterPreferencesHideFoundRequests &
  LFTypes.FilterPreferencesLoaded {
  const hideFoundRequestsState = useState<boolean | undefined>(false);

  const [hideFoundRequests, , , loaded] = useStoredValue<boolean>(
    StorageKey.ListingsFiltersHideFoundRequests,
    false,
    hideFoundRequestsState,
  );

  return {
    hideFoundRequestsState,
    hideFoundRequests: hideFoundRequests ?? false,
    loaded,
  };
}

function useFilterPreferencesHideOffersWithFee(): LFTypes.FilterPreferencesHideOffersWithFee &
  LFTypes.FilterPreferencesLoaded {
  const hideOffersWithFeeState = useState<boolean | undefined>(false);

  const [hideOffersWithFee, , , loaded] = useStoredValue<boolean>(
    StorageKey.ListingsFiltersHideOffersWithFee,
    false,
    hideOffersWithFeeState,
  );

  return {
    hideOffersWithFeeState,
    hideOffersWithFee: hideOffersWithFee ?? false,
    loaded,
  };
}

function useFilterPreferencesTransactionModes(): LFTypes.FilterPreferencesTransactionModes &
  LFTypes.FilterPreferencesLoaded {
  const transactionModesState = useState<TransactionMode[] | undefined>(DEFAULT_TRANSACTION_MODES);

  const [selectedTransactionModes, , , loaded] = useStoredValue<TransactionMode[]>(
    StorageKey.ListingsFiltersTransactionModes,
    DEFAULT_TRANSACTION_MODES,
    transactionModesState,
  );

  return {
    transactionModesState,
    selectedTransactionModes: selectedTransactionModes ?? DEFAULT_TRANSACTION_MODES,
    loaded,
  };
}

function useFilterPreferencesHideDismissedRequests(): LFTypes.FilterPreferencesHideDismissedRequests &
  LFTypes.FilterPreferencesLoaded {
  const dismissedOnlyState = useState<boolean | undefined>(false);

  const [dismissedOnly, , , loaded] = useStoredValue<boolean>(
    StorageKey.ListingsFiltersHideDismissedRequests,
    false,
    dismissedOnlyState,
  );

  return {
    dismissedOnlyState,
    dismissedOnly: dismissedOnly ?? false,
    loaded,
  };
}
// For offers, we can filter by distance, and specify a search center
function useFilterPreferencesSearchDistance(): LFTypes.FilterPreferencesSearchDistance &
  LFTypes.FilterPreferencesLoaded {
  const { data: searchDistancePreference } = useOnePreference('searchDistance');

  const incomingDistance = searchDistancePreference?.value ?? MAX_RADIUS_IN_METERS;

  const searchDistanceState = useSourceDraft(incomingDistance);
  const [searchDistance] = searchDistanceState;

  const { searchCenter } = useSearchCenter();

  return {
    searchDistance,
    searchDistanceState,
    searchCenter,
    loaded: Boolean(searchDistancePreference),
  };
}

function useSortingPref(pref: keyof PreferenceNameToValue, defaultValue = ListingsSortBy.Date) {
  // Load preference from API
  const { data: preference } = useOnePreference(pref);

  const prefValue = preference?.value as ListingsSortBy | undefined;

  const [sortValue, setSortDraft] = useSourceDraft<ListingsSortBy>(prefValue ?? defaultValue);
  const upsertSortServicesPreference = useUpsertOnePreference(pref);

  const setSortValue: React.Dispatch<React.SetStateAction<ListingsSortBy>> = (nextValue) => {
    const resolvedValue = typeof nextValue === 'function' ? nextValue(sortValue) : nextValue;
    setSortDraft(resolvedValue);
    if (preference?.value && preference.value !== resolvedValue && resolvedValue !== ListingsSortBy.Relevance) {
      upsertSortServicesPreference.mutate(resolvedValue);
    }
  };

  const state: [ListingsSortBy, React.Dispatch<React.SetStateAction<ListingsSortBy>>] = [sortValue, setSortValue];
  return { state, preference };
}

function useSortingPreferences(): LFTypes.SortingPreferences & LFTypes.FilterPreferencesLoaded {
  const { state: sortServicesState, preference: sortServicesPreference } = useSortingPref(
    'sortServices',
    ListingsSortBy.StartDate,
  );
  const { state: sortOffersState, preference: sortOffersPreference } = useSortingPref('sortOffers');
  const { state: sortNeedsState, preference: sortNeedsPreference } = useSortingPref('sortNeeds');
  return {
    sortServicesState,
    sortOffersState,
    sortNeedsState,
    loaded: Boolean(sortServicesPreference && sortOffersPreference && sortNeedsPreference),
  };
}

export function useFilterPreferences(
  defaultSelectedCategory?: Category['id'],
  defaultSelectedTag?: Tag['id'],
): LFTypes.FilterPreferences {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  // These atoms are set to `true` only after the BFF /user_profile response has been fully
  // processed and injected into the TanStack Query cache. They start `false` on every app
  // launch (in-memory atoms, NOT persisted). Using them in `isLoaded` prevents premature
  // fetches that happen when TQ returns stale persisted-cache data before the fresh BFF data
  // arrives (e.g. sort preference "date" from cache vs "distance" from server).
  const bffPreferencesLoaded = useAtomValue(preferencesLoadedAtom);
  const bffAddressesLoaded = useAtomValue(addressesLoadedAtom);
  const { data: addresses } = useAllAddresses();
  const { data: groups } = useAllGroups(true);
  const { data: categories } = useAllCategories();
  const { data: tags } = useAllTags();
  const { data: serviceCategories } = useAllServiceCategories();
  const subtypes = useAvailableSubtypes();
  const availableValuesLoaded = Boolean(addresses && groups && categories && tags && serviceCategories && subtypes);

  const { loaded: addressesLoaded, ...filterPreferencesAddresses } = useFilterPreferencesAddresses();
  const { loaded: groupsLoaded, ...filterPreferencesGroups } = useFilterPreferencesGroups();
  const { loaded: categoriesLoaded, ...filterPreferencesCategories } =
    useFilterPreferencesCategories(defaultSelectedCategory);
  const { loaded: _tagsLoaded, ...filterPreferencesTags } = useFilterPreferencesTags(defaultSelectedTag);
  const { loaded: serviceCategoriesLoaded, ...filterPreferencesServiceCategories } =
    useFilterPreferencesServiceCategories();
  const { loaded: subtypesLoaded, ...filterPreferencesSubtypes } = useFilterPreferencesSubtypes(subtypes);
  const { loaded: searchDistanceLoaded, ...filterPreferencesSearchDistance } = useFilterPreferencesSearchDistance();
  const { loaded: sortingPreferencesLoaded, ...sortingPreferences } = useSortingPreferences();
  const { loaded: hideFoundRequestsLoaded, ...filterPreferencesHideFoundRequests } =
    useFilterPreferencesHideFoundRequests();
  const { loaded: hideOffersWithFeeLoaded, ...filterPreferencesHideOffersWithFee } =
    useFilterPreferencesHideOffersWithFee();
  const { loaded: dismissedOnlyLoaded, ...filterPreferencesHideDismissedRequests } =
    useFilterPreferencesHideDismissedRequests();
  const { loaded: transactionModesLoaded, ...filterPreferencesTransactionModes } =
    useFilterPreferencesTransactionModes();

  const upsertSearchDistancePreference = useUpsertOnePreference('searchDistance');

  const setFilters: LFTypes.FiltersChangedType = useCallback(
    (newFilters: LFTypes.FilterPreferenceValues) => {
      const hasAudienceFilters =
        Object.prototype.hasOwnProperty.call(newFilters, 'selectedAddresses') ||
        Object.prototype.hasOwnProperty.call(newFilters, 'selectedGroups');

      if (
        hasAudienceFilters &&
        (!newFilters.selectedAddresses || newFilters.selectedAddresses.length === 0) &&
        (!newFilters.selectedGroups || newFilters.selectedGroups.length === 0)
      ) {
        newFilters.selectedAddresses = addresses?.map((a) => a.id) ?? [];
        newFilters.selectedGroups = groups?.map((g) => g.id) ?? [];
      }

      if (
        newFilters.selectedAddresses &&
        newFilters.selectedAddresses.join() !== filterPreferencesAddresses.selectedAddresses.join()
      ) {
        filterPreferencesAddresses.addressesState[1](newFilters.selectedAddresses);
      }

      if (
        newFilters.selectedGroups &&
        newFilters.selectedGroups.join() !== filterPreferencesGroups.selectedGroups.join()
      ) {
        filterPreferencesGroups.groupsState[1](newFilters.selectedGroups);
      }

      if (
        newFilters.selectedCategories &&
        newFilters.selectedCategories.join() !== filterPreferencesCategories.selectedCategories?.join()
      ) {
        filterPreferencesCategories.categoriesState[1](newFilters.selectedCategories);
      }

      if (newFilters.selectedTags && newFilters.selectedTags.join() !== filterPreferencesTags.selectedTags?.join()) {
        filterPreferencesTags.tagsState[1](newFilters.selectedTags);
      }

      if (
        newFilters.selectedServiceCategories &&
        newFilters.selectedServiceCategories.join() !==
          filterPreferencesServiceCategories.selectedServiceCategories?.join()
      ) {
        filterPreferencesServiceCategories.serviceCategoriesState[1](newFilters.selectedServiceCategories);
      }

      // No subtypes selected = all subtypes selected
      if (!newFilters.selectedSubtypes || newFilters.selectedSubtypes.length === 0) {
        newFilters.selectedSubtypes = [...subtypes];
      }

      if (newFilters.selectedSubtypes.join() !== filterPreferencesSubtypes.selectedSubtypes?.join()) {
        filterPreferencesSubtypes.subtypesState[1](newFilters.selectedSubtypes);
      }

      if (newFilters.searchDistance) {
        if (newFilters.searchDistance !== filterPreferencesSearchDistance.searchDistance) {
          // Other filters are saved locally only, but this one is saved on the server too. meh.
          upsertSearchDistancePreference.mutate(newFilters.searchDistance);
          filterPreferencesSearchDistance.searchDistanceState[1](newFilters.searchDistance);
        }
      }

      if (newFilters.hideFoundRequests !== undefined) {
        filterPreferencesHideFoundRequests.hideFoundRequestsState[1](newFilters.hideFoundRequests);
      }

      if (newFilters.hideOffersWithFee !== undefined) {
        filterPreferencesHideOffersWithFee.hideOffersWithFeeState[1](newFilters.hideOffersWithFee);
      }

      if (newFilters.dismissedOnly !== undefined) {
        filterPreferencesHideDismissedRequests.dismissedOnlyState[1](newFilters.dismissedOnly);
      }

      if (
        newFilters.selectedTransactionModes &&
        newFilters.selectedTransactionModes.join() !==
          (filterPreferencesTransactionModes.selectedTransactionModes ?? []).join()
      ) {
        filterPreferencesTransactionModes.transactionModesState[1](newFilters.selectedTransactionModes);
      }
    },
    [
      filterPreferencesAddresses.selectedAddresses,
      filterPreferencesAddresses.addressesState,
      filterPreferencesGroups.selectedGroups,
      filterPreferencesGroups.groupsState,
      filterPreferencesCategories.selectedCategories,
      filterPreferencesCategories.categoriesState,
      filterPreferencesTags.selectedTags,
      filterPreferencesTags.tagsState,
      filterPreferencesServiceCategories.selectedServiceCategories,
      filterPreferencesServiceCategories.serviceCategoriesState,
      filterPreferencesSubtypes.selectedSubtypes,
      filterPreferencesSubtypes.subtypesState,
      addresses,
      filterPreferencesHideDismissedRequests.dismissedOnlyState,
      groups,
      subtypes,
      filterPreferencesSearchDistance.searchDistance,
      filterPreferencesSearchDistance.searchDistanceState,
      upsertSearchDistancePreference,
      filterPreferencesHideFoundRequests.hideFoundRequestsState,
      filterPreferencesHideOffersWithFee.hideOffersWithFeeState,
      filterPreferencesTransactionModes.selectedTransactionModes,
      filterPreferencesTransactionModes.transactionModesState,
    ],
  );

  // Extract the value from each useState tuple so that `getFiltersForQuery`
  // only recreates when the actual value changes, not when the tuple reference does.
  const sortNeedsValue = sortingPreferences.sortNeedsState[0];
  const sortOffersValue = sortingPreferences.sortOffersState[0];
  const sortServicesValue = sortingPreferences.sortServicesState[0];
  const hideFoundRequestsValue = filterPreferencesHideFoundRequests.hideFoundRequestsState[0];
  const hideOffersWithFeeValue = filterPreferencesHideOffersWithFee.hideOffersWithFeeState[0];
  const dismissedOnlyValue = filterPreferencesHideDismissedRequests.dismissedOnlyState[0];
  const selectedTransactionModesValue = filterPreferencesTransactionModes.transactionModesState[0];

  const getFiltersForQuery: () => LFTypes.FilterPreferencesForQuery = useCallback(
    () => ({
      selectedAddresses: filterPreferencesAddresses.selectedAddresses,
      selectedGroups: filterPreferencesGroups.selectedGroups,
      selectedCategories: filterPreferencesCategories.selectedCategories,
      selectedTags: filterPreferencesTags.selectedTags,
      selectedServiceCategories: filterPreferencesServiceCategories.selectedServiceCategories,
      selectedSubtypes: filterPreferencesSubtypes.selectedSubtypes,
      searchDistance: filterPreferencesSearchDistance.searchDistance,
      searchCenter: filterPreferencesSearchDistance.searchCenter,
      sortNeeds: sortNeedsValue,
      sortOffers: sortOffersValue,
      sortServices: sortServicesValue,
      hideFoundRequests: hideFoundRequestsValue,
      dismissedOnly: dismissedOnlyValue,
      hideOffersWithFee: hideOffersWithFeeValue,
      selectedTransactionModes: selectedTransactionModesValue,
      availableValues: {
        addresses,
        groups,
        categories,
        tags,
        serviceCategories,
        subtypes,
      },
      isSaving: upsertSearchDistancePreference.isPending,
    }),
    [
      filterPreferencesAddresses.selectedAddresses,
      filterPreferencesGroups.selectedGroups,
      filterPreferencesCategories.selectedCategories,
      filterPreferencesTags.selectedTags,
      filterPreferencesServiceCategories.selectedServiceCategories,
      filterPreferencesSubtypes.selectedSubtypes,
      filterPreferencesSearchDistance.searchDistance,
      filterPreferencesSearchDistance.searchCenter,
      sortNeedsValue,
      sortOffersValue,
      sortServicesValue,
      hideFoundRequestsValue,
      dismissedOnlyValue,
      hideOffersWithFeeValue,
      selectedTransactionModesValue,
      addresses,
      groups,
      categories,
      tags,
      serviceCategories,
      subtypes,
      upsertSearchDistancePreference.isPending,
    ],
  );

  const filtersForQuery = useMemo(() => getFiltersForQuery(), [getFiltersForQuery]);

  const isLoaded = useMemo(
    () =>
      (!isLoggedIn ||
        // Those are only available for logged-in users.
        // bffPreferencesLoaded / bffAddressesLoaded ensure we wait for the FRESH BFF
        // data, not just the stale TanStack Query persisted cache. Without these, the
        // individual `loaded` flags below can be true from stale cache, causing premature
        // fetches that are immediately followed by corrective fetches when fresh data
        // arrives (e.g. sort changing from "date" to "distance").
        (bffPreferencesLoaded &&
          bffAddressesLoaded &&
          availableValuesLoaded &&
          addressesLoaded &&
          groupsLoaded &&
          Boolean(
            filterPreferencesAddresses.selectedAddresses.length || filterPreferencesGroups.selectedGroups.length,
          ) &&
          searchDistanceLoaded &&
          sortingPreferencesLoaded)) &&
      categoriesLoaded &&
      _tagsLoaded &&
      serviceCategoriesLoaded &&
      subtypesLoaded &&
      hideFoundRequestsLoaded &&
      dismissedOnlyLoaded &&
      hideOffersWithFeeLoaded &&
      transactionModesLoaded,
    [
      addressesLoaded,
      availableValuesLoaded,
      bffAddressesLoaded,
      bffPreferencesLoaded,
      categoriesLoaded,
      _tagsLoaded,
      filterPreferencesAddresses.selectedAddresses.length,
      filterPreferencesGroups.selectedGroups.length,
      groupsLoaded,
      hideFoundRequestsLoaded,
      dismissedOnlyLoaded,
      hideOffersWithFeeLoaded,
      transactionModesLoaded,
      isLoggedIn,
      searchDistanceLoaded,
      serviceCategoriesLoaded,
      sortingPreferencesLoaded,
      subtypesLoaded,
    ],
  );

  return {
    ...filterPreferencesAddresses,
    ...filterPreferencesGroups,
    ...filterPreferencesCategories,
    ...filterPreferencesTags,
    ...filterPreferencesServiceCategories,
    ...filterPreferencesSubtypes,
    ...filterPreferencesSearchDistance,
    ...filterPreferencesHideFoundRequests,
    ...filterPreferencesHideDismissedRequests,
    ...filterPreferencesHideOffersWithFee,
    ...filterPreferencesTransactionModes,
    ...sortingPreferences,
    availableValues: {
      addresses,
      groups,
      categories,
      tags,
      serviceCategories,
      subtypes,
    },
    setFilters,
    forQuery: filtersForQuery,
    isLoaded,
    isSaving: upsertSearchDistancePreference.isPending,
  };
}

export const useFiltersCopy = (filterPreferences: LFTypes.FilterPreferences, defaultDistance?: number) => {
  const [selectedAddresses, setSelectedAddresses] = useSourceDraft(filterPreferences.selectedAddresses);
  const [selectedGroups, setSelectedGroups] = useSourceDraft(filterPreferences.selectedGroups);
  const [selectedCategories, setSelectedCategories] = useSourceDraft(
    filterPreferences.selectedCategories ?? EMPTY_STRING_IDS,
  );
  const [selectedServiceCategories, setSelectedServiceCategories] = useSourceDraft(
    filterPreferences.selectedServiceCategories ?? EMPTY_STRING_IDS,
  );
  const [selectedSubtypes, setSelectedSubtypes] = useSourceDraft(
    filterPreferences.selectedSubtypes ?? EMPTY_LISTING_SUBTYPES,
  );
  const [selectedTags, setSelectedTags] = useSourceDraft(filterPreferences.selectedTags ?? EMPTY_STRING_IDS);
  const [searchDistance, setSearchDistance] = useSourceDraft(defaultDistance);
  const { searchCenter, saveSearchCenter } = useSearchCenter();
  const [hideFoundRequests, setHideFoundRequests] = useSourceDraft(filterPreferences.hideFoundRequests);
  const [dismissedOnly, setDismissedOnly] = useSourceDraft(filterPreferences.dismissedOnly);
  const [hideOffersWithFee, setHideOffersWithFee] = useSourceDraft(filterPreferences.hideOffersWithFee);
  const [selectedTransactionModes, setSelectedTransactionModes] = useSourceDraft(
    filterPreferences.selectedTransactionModes ?? EMPTY_TRANSACTION_MODES,
  );

  const toggleAddress = useCallback(
    (addressId: string) => {
      setSelectedAddresses((currentSelectedAddresses) => {
        if (currentSelectedAddresses.includes(addressId)) {
          return currentSelectedAddresses.filter((filter) => filter !== addressId);
        }
        return [...currentSelectedAddresses, addressId];
      });
    },
    [setSelectedAddresses],
  );

  const toggleGroup = useCallback(
    (groupId: string) => {
      setSelectedGroups((currentSelectedGroups) => {
        if (currentSelectedGroups.includes(groupId)) {
          return currentSelectedGroups.filter((filter) => filter !== groupId);
        }
        return [...currentSelectedGroups, groupId];
      });
    },
    [setSelectedGroups],
  );

  const toggleSubtype = useCallback(
    (subType: ListingSubtype) => {
      setSelectedSubtypes((currentSelectedSubtypes) => {
        if (currentSelectedSubtypes.includes(subType)) {
          return currentSelectedSubtypes.filter((filter) => filter !== subType);
        }
        return [...currentSelectedSubtypes, subType];
      });
    },
    [setSelectedSubtypes],
  );

  const toggleFoundRequests = useCallback(() => {
    setHideFoundRequests((oldValue) => !oldValue);
  }, [setHideFoundRequests]);

  const toggleDismissedRequests = useCallback(() => {
    setDismissedOnly((oldValue) => !oldValue);
  }, [setDismissedOnly]);

  const toggleOffersWithFee = useCallback(() => {
    setHideOffersWithFee((oldValue) => !oldValue);
  }, [setHideOffersWithFee]);

  const toggleTransactionMode = useCallback(
    (mode: TransactionMode) => {
      setSelectedTransactionModes((current) => {
        if (current.includes(mode)) {
          return current.filter((m) => m !== mode);
        }
        return [...current, mode];
      });
    },
    [setSelectedTransactionModes],
  );

  const resetFilters = useCallback(() => {
    const resetSearchDistance = defaultDistance ?? searchDistance;
    const defaults = applyDefaultFilterValues(
      filterPreferences.availableValues,
      resetSearchDistance,
      filterPreferences.setFilters,
    );
    setSelectedAddresses(defaults.selectedAddresses);
    setSelectedGroups(defaults.selectedGroups);
    setSelectedCategories(defaults.selectedCategories);
    setSelectedServiceCategories(defaults.selectedServiceCategories);
    setSelectedSubtypes(defaults.selectedSubtypes);
    setSelectedTags(defaults.selectedTags);
    setHideFoundRequests(defaults.hideFoundRequests);
    setDismissedOnly(defaults.dismissedOnly);
    setHideOffersWithFee(defaults.hideOffersWithFee);
    setSelectedTransactionModes(defaults.selectedTransactionModes);
    setSearchDistance(resetSearchDistance);
  }, [
    defaultDistance,
    filterPreferences,
    searchDistance,
    setDismissedOnly,
    setHideFoundRequests,
    setHideOffersWithFee,
    setSearchDistance,
    setSelectedAddresses,
    setSelectedCategories,
    setSelectedGroups,
    setSelectedServiceCategories,
    setSelectedSubtypes,
    setSelectedTags,
    setSelectedTransactionModes,
  ]);

  const onSave = useCallback(
    (
      newSearchDistance?: number,
      newSearchCenter?: LatLng,
      newCategories?: string[],
      newTags?: string[],
      newServiceCategories?: string[],
      newSubtypes?: ListingSubtype[],
      newHideFoundRequests?: boolean,
      newDismissedOnly?: boolean,
      newHideOffersWithFee?: boolean,
      newTransactionModes?: TransactionMode[],
    ) => {
      if (newCategories) {
        setSelectedCategories(newCategories);
      }
      if (newTags) {
        setSelectedTags(newTags);
      }
      if (newServiceCategories) {
        setSelectedServiceCategories(newServiceCategories);
      }
      if (newSubtypes) {
        setSelectedSubtypes(newSubtypes);
      }
      if (newTransactionModes) {
        setSelectedTransactionModes(newTransactionModes);
      }
      if (selectedAddresses.length > 0) {
        if (newSearchDistance) {
          setSearchDistance(newSearchDistance);
        }
        if (newSearchCenter && selectedAddresses.length === 1) {
          saveSearchCenter(newSearchCenter);
        }
      }

      // Apply the filters
      filterPreferences.setFilters({
        selectedAddresses: selectedAddresses,
        selectedGroups: selectedGroups,
        selectedCategories: newCategories ?? selectedCategories,
        selectedTags: newTags ?? selectedTags,
        selectedServiceCategories: newServiceCategories ?? selectedServiceCategories,
        selectedSubtypes: newSubtypes ?? selectedSubtypes,
        searchDistance: newSearchDistance ?? searchDistance,
        hideFoundRequests: newHideFoundRequests ?? hideFoundRequests,
        dismissedOnly: newDismissedOnly ?? dismissedOnly,
        hideOffersWithFee: newHideOffersWithFee ?? hideOffersWithFee,
        selectedTransactionModes: newTransactionModes ?? selectedTransactionModes,
      });
    },
    [
      filterPreferences,
      hideFoundRequests,
      dismissedOnly,
      hideOffersWithFee,
      saveSearchCenter,
      searchDistance,
      selectedAddresses,
      selectedCategories,
      selectedGroups,
      selectedServiceCategories,
      selectedSubtypes,
      selectedTags,
      selectedTransactionModes,
      setSearchDistance,
      setSelectedCategories,
      setSelectedServiceCategories,
      setSelectedSubtypes,
      setSelectedTags,
      setSelectedTransactionModes,
    ],
  );

  return {
    selectedAddresses,
    selectedGroups,
    selectedCategories,
    selectedServiceCategories,
    selectedSubtypes,
    selectedTags,
    hideFoundRequests,
    dismissedOnly,
    hideOffersWithFee,
    selectedTransactionModes,
    toggleAddress,
    toggleGroup,
    toggleSubtype,
    toggleFoundRequests,
    toggleDismissedRequests,
    toggleOffersWithFee,
    toggleTransactionMode,
    resetFilters,
    onSave,
    searchDistance,
    searchCenter,
  };
};
